import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';

import { axiosPrivate } from 'app/utils';

import type { OnboardingJourney, PlusWebsitesResponse } from '../../../hooks/usePlusWebsites';

interface UpdateWebsiteOnboardingJourneyParams {
  websiteId: string;
  onboardingJourney: OnboardingJourney;
}

function updateWebsiteOnboardingJourney({
  websiteId,
  onboardingJourney,
}: UpdateWebsiteOnboardingJourneyParams) {
  return axiosPrivate
    .put(`/v3/support/plus-websites/${websiteId}/onboarding-journeys`, { onboardingJourney })
    .then((r) => r.data);
}

export function useUpdateWebsiteOnboardingJourney() {
  const queryClient = useQueryClient();
  const queryKey = ['plus-websites'];

  return useMutation({
    mutationFn: updateWebsiteOnboardingJourney,
    // Deliberately no invalidation after the write: the list endpoint can lag
    // behind this PUT, so an immediate refetch may return the old value and
    // overwrite this cache update (and refetchOnWindowFocus is disabled, so it
    // would stick until a remount). The accepted PUT is the source of truth.
    onSuccess: (_data, { websiteId, onboardingJourney }: UpdateWebsiteOnboardingJourneyParams) => {
      queryClient.setQueriesData<AxiosResponse<PlusWebsitesResponse>>({ queryKey }, (old) =>
        old
          ? {
              ...old,
              data: {
                ...old.data,
                plusWebsites: old.data.plusWebsites.map((website) =>
                  website.id === websiteId ? { ...website, onboardingJourney } : website
                ),
              },
            }
          : old
      );
    },
    meta: {
      successMessage: 'Onboarding journey updated successfully',
      errorMessage: 'Failed to update onboarding journey',
    },
  });
}
